// Thanh nút nổi của Bộ soi ảnh. Bản gọn (khung nhỏ, điện thoại) giữ − + Vừa khung và gom phần
// còn lại vào nút "⋯".
import { useState, type ReactNode } from "react";
import { Ellipsis, Expand, Keyboard, Search, Shrink, Wrench, ZoomIn, ZoomOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { BG_LABEL, MODE_LABEL, type CompareMode, type ViewerBg } from "./types";

export type ToolbarProps = {
  compact: boolean;
  percentLabel: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  onActual: () => void;
  loupeOn: boolean;
  onToggleLoupe: () => void;
  loupeDisabled?: string | undefined;
  modes?: CompareMode[] | undefined;
  mode: CompareMode;
  onMode: (m: CompareMode) => void;
  onFlickerHold: (held: boolean) => void;
  fullscreen: boolean;
  onFullscreen: () => void;
  bg: ViewerBg;
  onBg: (bg: ViewerBg) => void;
  toolsOpen: boolean;
  onToggleTools: () => void;
  helpOpen: boolean;
  onToggleHelp: () => void;
};

function Btn({
  label,
  onClick,
  active,
  disabled,
  children,
  className,
  ...rest
}: {
  label: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: string | undefined;
  children: ReactNode;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "disabled">) {
  return (
    <button
      type="button"
      aria-label={label}
      title={disabled ?? label}
      aria-pressed={active}
      disabled={!!disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-9 min-w-9 items-center justify-center gap-1 rounded-full px-2 text-xs font-medium text-white hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40",
        active && "bg-white/25",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function ViewerToolbar(p: ToolbarProps) {
  const [more, setMore] = useState(false);
  const rest = (
    <>
      <Btn label="Xem cỡ thật 1:1 — mỗi điểm ảnh một điểm màn hình (phím 1)" onClick={p.onActual}>
        1:1
      </Btn>
      <Btn
        label="Kính lúp — giữ phím Z rồi di chuột, hoặc chạm giữ"
        active={p.loupeOn}
        disabled={p.loupeDisabled}
        onClick={p.onToggleLoupe}
      >
        <Search className="h-4 w-4" aria-hidden />
        {p.compact ? <span>Kính lúp</span> : null}
      </Btn>
      {p.modes && p.modes.length > 1 ? (
        <label className="inline-flex items-center gap-1 px-1 text-xs text-white">
          <span className="sr-only">Cách so</span>
          <select
            aria-label="Cách so hai bản"
            value={p.mode}
            onChange={(e) => p.onMode(e.target.value as CompareMode)}
            className="h-8 rounded-full border border-white/30 bg-black/40 px-2 text-xs text-white"
          >
            {p.modes.map((m) => (
              <option key={m} value={m}>
                {MODE_LABEL[m]}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {p.mode === "flicker" ? (
        <Btn
          label="Giữ để xem bản kia (phím B)"
          onPointerDown={() => p.onFlickerHold(true)}
          onPointerUp={() => p.onFlickerHold(false)}
          onPointerLeave={() => p.onFlickerHold(false)}
          className="border border-white/40"
        >
          Giữ xem bản kia
        </Btn>
      ) : null}
      <label className="inline-flex items-center px-1">
        <span className="sr-only">Nền soi</span>
        <select
          aria-label="Nền soi"
          value={p.bg}
          onChange={(e) => p.onBg(e.target.value as ViewerBg)}
          className="h-8 rounded-full border border-white/30 bg-black/40 px-2 text-xs text-white"
        >
          {(Object.keys(BG_LABEL) as ViewerBg[]).map((b) => (
            <option key={b} value={b}>
              {BG_LABEL[b]}
            </option>
          ))}
        </select>
      </label>
      <Btn label="Công cụ soi: biểu đồ sáng, báo cháy, ống hút màu, lưới, xoay" active={p.toolsOpen} onClick={() => {
          // Bảng công cụ phủ lên khung: để menu ⋯ mở thì hai lớp chồng nhau trên điện thoại.
          setMore(false);
          p.onToggleTools();
        }}>
        <Wrench className="h-4 w-4" aria-hidden />
        <span>Công cụ</span>
      </Btn>
      <Btn label={p.fullscreen ? "Thoát toàn màn (phím F)" : "Toàn màn hình (phím F)"} onClick={p.onFullscreen}>
        {p.fullscreen ? <Shrink className="h-4 w-4" aria-hidden /> : <Expand className="h-4 w-4" aria-hidden />}
      </Btn>
      <Btn label="Phím tắt (phím ?)" active={p.helpOpen} onClick={() => { setMore(false); p.onToggleHelp(); }} className="hidden md:inline-flex">
        <Keyboard className="h-4 w-4" aria-hidden />
      </Btn>
    </>
  );

  return (
    <div
      role="toolbar"
      aria-label="Thanh soi ảnh"
      className="pointer-events-auto absolute bottom-2 left-1/2 z-20 flex max-w-[calc(100%-1rem)] -translate-x-1/2 flex-col items-center gap-1"
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {p.compact && more ? (
        <div className="flex max-w-full flex-wrap items-center justify-center gap-1 rounded-2xl bg-black/70 p-1 backdrop-blur">
          {rest}
        </div>
      ) : null}
      <div className="flex max-w-full flex-wrap items-center justify-center gap-0.5 rounded-full bg-black/65 p-1 shadow-lg backdrop-blur">
        <Btn label="Thu nhỏ (phím −)" onClick={p.onZoomOut}>
          <ZoomOut className="h-4 w-4" aria-hidden />
        </Btn>
        <span className="min-w-12 text-center text-xs tabular-nums text-white" aria-live="polite">
          {p.percentLabel}
        </span>
        <Btn label="Phóng to (phím +)" onClick={p.onZoomIn}>
          <ZoomIn className="h-4 w-4" aria-hidden />
        </Btn>
        <Btn label="Vừa khung (phím 0)" onClick={p.onFit}>
          Vừa khung
        </Btn>
        {p.compact ? (
          <Btn label="Thêm công cụ soi" active={more} onClick={() => setMore((v) => !v)}>
            <Ellipsis className="h-4 w-4" aria-hidden />
          </Btn>
        ) : (
          rest
        )}
      </div>
    </div>
  );
}

export function HelpPanel({ onClose }: { onClose: () => void }) {
  const rows: [string, string][] = [
    ["Lăn chuột / chụm 2 ngón", "Phóng to, thu nhỏ quanh chỗ con trỏ"],
    ["Kéo", "Di chuyển khi đã phóng"],
    ["Bấm đúp", "Vừa khung ↔ cỡ thật 1:1"],
    ["+  /  −", "Phóng / thu một nấc"],
    ["0  /  1", "Vừa khung / cỡ thật 1:1"],
    ["Giữ Z", "Kính lúp cỡ thật dưới con trỏ"],
    ["Giữ B", "Nháy sang bản kia (khi so hai bản)"],
    ["F", "Toàn màn hình"],
    ["← →", "Ảnh trước / sau (khi xem nhiều ảnh)"],
  ];
  return (
    <div
      role="dialog"
      aria-label="Phím tắt bộ soi ảnh"
      className="absolute right-2 top-2 z-30 w-72 max-w-[calc(100%-1rem)] rounded-lg bg-black/85 p-3 text-xs text-white shadow-xl"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="mb-2 flex items-center justify-between">
        <strong>Phím tắt</strong>
        <button type="button" className="rounded px-2 py-1 hover:bg-white/15" onClick={onClose} aria-label="Đóng bảng phím tắt">
          ✕
        </button>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="font-mono text-white/90">{k}</dt>
            <dd className="text-white/75">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
